import React, { Component, Suspense, lazy, useEffect } from "react";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import DockLayout from "./core/layout/DockLayout";
import ThemeProvider from "./core/theme/ThemeProvider";
import GlobalLoadingPage from "./core/components/GlobalLoadingPage";
import DynamicInstallMetadata from "./core/pwa/DynamicInstallMetadata";
import { TilePreferencesProvider } from "./core/tiles/TilePreferencesProvider";
import { useAuth } from "./auth/useAuth";
import ChurchAuthProvider from "./platforms/church/auth/ChurchAuthProvider";
import ChurchRequireAuth from "./platforms/church/auth/ChurchRequireAuth";
import ChurchLoginPage from "./platforms/church/auth/ChurchLoginPage";
import ChurchLiveDisplayViewerPage from "./platforms/church/pages/ChurchLiveDisplayViewerPage";
import { isOikosChurchSalesHost } from "./core/utils/modeRouting";

const load = (importer, path) =>
  lazy(() =>
    importer().catch((error) => ({
      default: () => (
        <div style={{ padding: 20 }}>
          <h2>Could not load this page</h2>
          <pre style={{ whiteSpace: "pre-wrap" }}>{error?.message || `Page not built yet: ${path}`}</pre>
        </div>
      ),
    }))
  );

const importChurchDashboard = () => import("./platforms/church/pages/ChurchDashboardPage");
const importChurchFamilyPage = () => import("./platforms/church/pages/ChurchFamilyPage");
const importChurchLanding = () => import("./platforms/church/pages/ChurchLandingPage");
const importOikosChurchSalesPage = () => import("./platforms/church/pages/OikosChurchSalesPage");
const importChurchWebsitePage = () => import("./platforms/church/pages/ChurchWebsitePage");
const importChurchMeetingMinutesPage = () => import("./platforms/church/pages/ChurchMeetingMinutesPage");
const importChurchMeetingMinutesPadPage = () => import("./platforms/church/pages/ChurchMeetingMinutesPadPage");
const importGlobalAdminPage = () => import("./platforms/church/pages/GlobalAdminPage");
const importSermonPage = () => import("./platforms/church/tiles/Sermon/SermonPage");
const importHymnsPage = () => import("./platforms/church/tiles/Hymns/HymnsPage");
const importAnnouncementPage = () => import("./platforms/church/tiles/announcements/AnnouncementPage");
const importAttendancePage = () => import("./platforms/church/tiles/Attendance/AttendancePage");
const importEventsPage = () => import("./platforms/church/tiles/Events/EventsPage");
const importFeedbackPage = () => import("./platforms/church/tiles/Feedback/FeedbackPage");
const importMensDutiesPage = () => import("./platforms/church/tiles/MensDuties/MensDutiesPage");
const importServicePage = () => import("./platforms/church/tiles/Service/ServicePage");
const importServiceControllerPage = () => import("./platforms/church/tiles/Service/ServiceControllerPage");
const importLiveDisplayPage = () => import("./platforms/church/tiles/LiveDisplay/LiveDisplayPage");
const importStreamPage = () => import("./platforms/church/tiles/Stream/StreamPage");
const importChurchControllers = () => import("./platforms/church/tiles/Remote/RemoteApp");
const importControllerLandingPage = () => import("./platforms/church/tiles/Remote/ControllerLandingPage");
const routePreloaders = [
  importChurchLanding,
  importOikosChurchSalesPage,
  importChurchWebsitePage,
  importChurchDashboard,
  importChurchFamilyPage,
  importHymnsPage,
  importLiveDisplayPage,
  importAnnouncementPage,
  importAttendancePage,
  importEventsPage,
  importFeedbackPage,
  importMensDutiesPage,
  importServicePage,
  importServiceControllerPage,
  importSermonPage,
  importStreamPage,
  importChurchControllers,
  importControllerLandingPage,
  importChurchMeetingMinutesPage,
  importChurchMeetingMinutesPadPage,
  importGlobalAdminPage,
];

const ChurchDashboard = load(
  importChurchDashboard,
  "./platforms/church/pages/ChurchDashboardPage"
);
const ChurchLanding = load(
  importChurchLanding,
  "./platforms/church/pages/ChurchLandingPage"
);
const OikosChurchSalesPage = load(
  importOikosChurchSalesPage,
  "./platforms/church/pages/OikosChurchSalesPage"
);
const ChurchWebsitePage = load(
  importChurchWebsitePage,
  "./platforms/church/pages/ChurchWebsitePage"
);
const ChurchFamilyPage = load(
  importChurchFamilyPage,
  "./platforms/church/pages/ChurchFamilyPage"
);
const ChurchMeetingMinutesPadPage = load(
  importChurchMeetingMinutesPadPage,
  "./platforms/church/pages/ChurchMeetingMinutesPadPage"
);
const ChurchMeetingMinutesPage = load(
  importChurchMeetingMinutesPage,
  "./platforms/church/pages/ChurchMeetingMinutesPage"
);
const GlobalAdminPage = load(
  importGlobalAdminPage,
  "./platforms/church/pages/GlobalAdminPage"
);
const SermonPage = load(
  importSermonPage,
  "./platforms/church/tiles/Sermon/SermonPage"
);
const HymnsPage = load(
  importHymnsPage,
  "./platforms/church/tiles/Hymns/HymnsPage"
);
const AnnouncementPage = load(
  importAnnouncementPage,
  "./platforms/church/tiles/announcements/AnnouncementPage"
);
const AttendancePage = load(
  importAttendancePage,
  "./platforms/church/tiles/Attendance/AttendancePage"
);
const EventsPage = load(
  importEventsPage,
  "./platforms/church/tiles/Events/EventsPage"
);
const FeedbackPage = load(
  importFeedbackPage,
  "./platforms/church/tiles/Feedback/FeedbackPage"
);
const MensDutiesPage = load(
  importMensDutiesPage,
  "./platforms/church/tiles/MensDuties/MensDutiesPage"
);
const ServicePage = load(
  importServicePage,
  "./platforms/church/tiles/Service/ServicePage"
);
const ServiceControllerPage = load(
  importServiceControllerPage,
  "./platforms/church/tiles/Service/ServiceControllerPage"
);
const LiveDisplayPage = load(
  importLiveDisplayPage,
  "./platforms/church/tiles/LiveDisplay/LiveDisplayPage"
);
const StreamPage = load(
  importStreamPage,
  "./platforms/church/tiles/Stream/StreamPage"
);
const ChurchControllers = load(
  importChurchControllers,
  "./platforms/church/tiles/Remote/RemoteApp"
);
const ControllerLandingPage = load(
  importControllerLandingPage,
  "./platforms/church/tiles/Remote/ControllerLandingPage"
);

function preloadRouteModules() {
  routePreloaders.forEach((preload) => {
    preload().catch(() => {});
  });
}

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(previousProps) {
    if (previousProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={routeErrorStyles.page}>
        <div style={routeErrorStyles.card}>
          <h1 style={routeErrorStyles.title}>Church could not finish loading</h1>
          <pre style={routeErrorStyles.error}>
            {this.state.error?.message || "Unknown route error"}
          </pre>
        </div>
      </div>
    );
  }
}

function ChurchTheme({ children }) {
  return <ThemeProvider mode="church">{children}</ThemeProvider>;
}

function ProtectedChurchAccess({ children }) {
  return (
    <ChurchAuthProvider>
      <ChurchRequireAuth>
        <TilePreferencesProvider>{children}</TilePreferencesProvider>
      </ChurchRequireAuth>
    </ChurchAuthProvider>
  );
}

function ProtectedChurchShell({ children }) {
  return (
    <ProtectedChurchAccess>
      <ChurchTheme>{children}</ChurchTheme>
    </ProtectedChurchAccess>
  );
}

function hasChurchAppAccess(user, appIds = []) {
  if (!user?.isChurchOrgUser) return true;

  const orgUser = user?.churchOrgSession?.orgUser;
  const role = String(orgUser?.role || user?.role || "").toLowerCase();
  if (role === "admin" || role === "owner") return true;

  const access =
    orgUser?.permissions ||
    user?.permissions ||
    orgUser?.metadata?.access ||
    user?.metadata?.access ||
    null;
  const allowedAppIds = Array.isArray(access?.appIds) ? access.appIds : [];
  return appIds.some((appId) => allowedAppIds.includes(appId));
}

function ChurchAppAccessGate({ appIds = [], children }) {
  const { user } = useAuth();

  if (!hasChurchAppAccess(user, appIds)) {
    return <Navigate to="/church" replace />;
  }

  return children;
}

function ChurchAdminRedirect() {
  const location = useLocation();
  return <Navigate to={`/church/management${location.search}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const path = location.pathname;
  const hostname = typeof window === "undefined" ? "" : String(window.location.hostname || "").toLowerCase();
  const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
  const isSalesHost = isOikosChurchSalesHost(hostname) || isLocalHost;

  if (path === "/login") {
    return <Navigate to="/church/login" replace />;
  }

  if ((path === "/" && isSalesHost) || (path === "/sales" && isSalesHost)) {
    return (
      <ChurchTheme>
        <Routes>
          <Route path="/" element={<OikosChurchSalesPage />} />
          <Route path="/sales" element={<OikosChurchSalesPage />} />
        </Routes>
      </ChurchTheme>
    );
  }

  if (path === "/" || path === "" || path === "/church/site" || path.startsWith("/church/site/")) {
    return (
      <ChurchTheme>
        <Routes>
          <Route path="/" element={<ChurchWebsitePage />} />
          <Route path="/church/site" element={<ChurchWebsitePage />} />
          <Route path="/church/site/:orgId" element={<ChurchWebsitePage />} />
          <Route path="*" element={<ChurchWebsitePage />} />
        </Routes>
      </ChurchTheme>
    );
  }

  if (path === "/church/login" || path.startsWith("/church/login/")) {
    return (
      <Routes>
        <Route path="/church/login" element={<ChurchLoginPage />} />
        <Route path="/church/login/:orgId" element={<ChurchLoginPage />} />
        <Route path="*" element={<Navigate to="/church/login" replace />} />
      </Routes>
    );
  }

  if (path === "/live" || path.startsWith("/live/")) {
    return (
      <ChurchTheme>
        <Routes>
          <Route path="/live" element={<ChurchLiveDisplayViewerPage />} />
          <Route path="/live/:displayCode" element={<ChurchLiveDisplayViewerPage />} />
          <Route path="/live/:displayCode/interactive/:interactiveAppId" element={<ChurchLiveDisplayViewerPage />} />
          <Route path="*" element={<Navigate to="/church" replace />} />
        </Routes>
      </ChurchTheme>
    );
  }

  if (path.startsWith("/church/controllers")) {
    return (
      <ProtectedChurchShell>
        <Routes>
          <Route path="/church/controllers" element={<Navigate to="/church/controllers/main" replace />} />
          <Route path="/church/controllers/preacher" element={<Navigate to="/sermon" replace />} />
          <Route path="/church/controllers/preacher/:orgId" element={<Navigate to="/sermon" replace />} />
          <Route path="/church/controllers/:controller" element={<ChurchAppAccessGate appIds={["controllers"]}><ChurchControllers standalone /></ChurchAppAccessGate>} />
          <Route path="/church/controllers/:controller/:orgId" element={<ChurchAppAccessGate appIds={["controllers"]}><ChurchControllers standalone /></ChurchAppAccessGate>} />
          <Route path="*" element={<Navigate to="/church/controllers/main" replace />} />
        </Routes>
      </ProtectedChurchShell>
    );
  }

  if (path === "/hymn" || path.startsWith("/hymn/")) {
    return <Navigate to="/hymns/controler" replace />;
  }

  if (path === "/hymns/controler" || path.startsWith("/hymns/controler/")) {
    return (
      <ProtectedChurchShell>
        <Routes>
          <Route path="/hymns/controler" element={<ChurchAppAccessGate appIds={["controllers", "hymns"]}><ChurchControllers initialController="hymns" standalone /></ChurchAppAccessGate>} />
          <Route path="/hymns/controler/:orgId" element={<ChurchAppAccessGate appIds={["controllers", "hymns"]}><ChurchControllers initialController="hymns" standalone /></ChurchAppAccessGate>} />
          <Route path="*" element={<Navigate to="/hymns/controler" replace />} />
        </Routes>
      </ProtectedChurchShell>
    );
  }

  if (path === "/globaladmin" || path.startsWith("/globaladmin/")) {
    return (
      <ProtectedChurchAccess>
        <ThemeProvider mode="admin">
          <Routes>
            <Route path="/globaladmin" element={<GlobalAdminPage />} />
            <Route path="/globaladmin/:sectionId" element={<GlobalAdminPage />} />
            <Route path="*" element={<Navigate to="/globaladmin" replace />} />
          </Routes>
        </ThemeProvider>
      </ProtectedChurchAccess>
    );
  }

  if (path === "/live-stream" || path.startsWith("/live-stream/")) {
    return <Navigate to="/church/stream" replace />;
  }

  if (path.startsWith("/church/management/minutes-pad")) {
    return (
      <ProtectedChurchShell>
        <Routes>
          <Route path="/church/management/minutes-pad" element={<ChurchMeetingMinutesPadPage />} />
          <Route path="/church/management/minutes-pad/:orgId" element={<ChurchMeetingMinutesPadPage />} />
          <Route path="*" element={<Navigate to="/church/management/minutes-pad" replace />} />
        </Routes>
      </ProtectedChurchShell>
    );
  }

  return (
    <ProtectedChurchShell>
      <DockLayout>
        <Routes>
          <Route path="/church" element={<ChurchLanding />} />
          <Route path="/church/:orgId" element={<ChurchLanding />} />
          <Route path="/church/family" element={<ChurchAppAccessGate appIds={["manage-family"]}><ChurchFamilyPage /></ChurchAppAccessGate>} />
          <Route path="/church/family/:orgId" element={<ChurchAppAccessGate appIds={["manage-family"]}><ChurchFamilyPage /></ChurchAppAccessGate>} />
          <Route path="/church/management" element={<ChurchDashboard />} />
          <Route path="/church/management/:orgId" element={<ChurchDashboard />} />
          <Route path="/church/admin" element={<ChurchAdminRedirect />} />
          <Route path="/controler" element={<ChurchAppAccessGate appIds={["controllers"]}><ControllerLandingPage /></ChurchAppAccessGate>} />
          <Route path="/controler/:orgId" element={<ChurchAppAccessGate appIds={["controllers"]}><ControllerLandingPage /></ChurchAppAccessGate>} />
          <Route path="/comtroler" element={<ChurchAppAccessGate appIds={["controllers"]}><ControllerLandingPage /></ChurchAppAccessGate>} />
          <Route path="/comtroler/:orgId" element={<ChurchAppAccessGate appIds={["controllers"]}><ControllerLandingPage /></ChurchAppAccessGate>} />
          <Route path="/sermon" element={<ChurchAppAccessGate appIds={["sermon"]}><SermonPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/sermon/:orgId" element={<ChurchAppAccessGate appIds={["sermon"]}><SermonPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route
            path="/sermon/editor"
            element={<ChurchAppAccessGate appIds={["sermon"]}><SermonPage initialViewMode="builder" useStandaloneRoutes /></ChurchAppAccessGate>}
          />
          <Route
            path="/sermon/editor/:orgId"
            element={<ChurchAppAccessGate appIds={["sermon"]}><SermonPage initialViewMode="builder" useStandaloneRoutes /></ChurchAppAccessGate>}
          />
          <Route path="/sermon/*" element={<ChurchAppAccessGate appIds={["sermon"]}><SermonPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route
            path="/hymns/selector"
            element={<ChurchAppAccessGate appIds={["hymns"]}><HymnsPage initialSection="selector" useStandaloneRoutes /></ChurchAppAccessGate>}
          />
          <Route
            path="/hymns/selector/:orgId"
            element={<ChurchAppAccessGate appIds={["hymns"]}><HymnsPage initialSection="selector" useStandaloneRoutes /></ChurchAppAccessGate>}
          />
          <Route
            path="/hymns/settings"
            element={<ChurchAppAccessGate appIds={["hymns"]}><HymnsPage initialSection="settings" useStandaloneRoutes /></ChurchAppAccessGate>}
          />
          <Route
            path="/hymns/settings/:orgId"
            element={<ChurchAppAccessGate appIds={["hymns"]}><HymnsPage initialSection="settings" useStandaloneRoutes /></ChurchAppAccessGate>}
          />
          <Route path="/hymns" element={<ChurchAppAccessGate appIds={["hymns"]}><HymnsPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/hymns/:orgId" element={<ChurchAppAccessGate appIds={["hymns"]}><HymnsPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/announcements" element={<ChurchAppAccessGate appIds={["announcements"]}><AnnouncementPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/announcements/:orgId" element={<ChurchAppAccessGate appIds={["announcements"]}><AnnouncementPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/attendance" element={<ChurchAppAccessGate appIds={["attendance"]}><AttendancePage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/attendance/:orgId" element={<ChurchAppAccessGate appIds={["attendance"]}><AttendancePage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/events" element={<ChurchAppAccessGate appIds={["events"]}><EventsPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/events/:orgId" element={<ChurchAppAccessGate appIds={["events"]}><EventsPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/feedback/:orgId" element={<FeedbackPage />} />
          <Route path="/mens-duties" element={<MensDutiesPage useStandaloneRoutes />} />
          <Route path="/mens-duties/:orgId" element={<MensDutiesPage useStandaloneRoutes />} />
          <Route path="/meeting-minutes" element={<ChurchAppAccessGate appIds={["minutes"]}><ChurchMeetingMinutesPage /></ChurchAppAccessGate>} />
          <Route path="/meeting-minutes/:orgId" element={<ChurchAppAccessGate appIds={["minutes"]}><ChurchMeetingMinutesPage /></ChurchAppAccessGate>} />
          <Route path="/service" element={<ChurchAppAccessGate appIds={["service"]}><ServicePage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/service/:orgId" element={<ChurchAppAccessGate appIds={["service"]}><ServicePage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/service/controler" element={<ChurchAppAccessGate appIds={["service", "controllers"]}><ServiceControllerPage /></ChurchAppAccessGate>} />
          <Route path="/service/controler/:orgId" element={<ChurchAppAccessGate appIds={["service", "controllers"]}><ServiceControllerPage /></ChurchAppAccessGate>} />
          <Route path="/live-display" element={<ChurchAppAccessGate appIds={["live-display"]}><LiveDisplayPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/live-display/:orgId" element={<ChurchAppAccessGate appIds={["live-display"]}><LiveDisplayPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/church/stream" element={<ChurchAppAccessGate appIds={["church-stream", "stream", "live-stream"]}><StreamPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/church/stream/:orgId" element={<ChurchAppAccessGate appIds={["church-stream", "stream", "live-stream"]}><StreamPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/church/live-stream" element={<ChurchAppAccessGate appIds={["church-stream", "stream", "live-stream"]}><StreamPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="/church/live-stream/:orgId" element={<ChurchAppAccessGate appIds={["church-stream", "stream", "live-stream"]}><StreamPage useStandaloneRoutes /></ChurchAppAccessGate>} />
          <Route path="*" element={<Navigate to="/church" replace />} />
        </Routes>
      </DockLayout>
    </ProtectedChurchShell>
  );
}

function AppShell() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const runPreload = () => preloadRouteModules();

    if (typeof window.requestIdleCallback === "function") {
      const idleId = window.requestIdleCallback(runPreload, { timeout: 2000 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(runPreload, 750);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <Suspense
      fallback={
        <GlobalLoadingPage
          title="Loading Church"
          detail="Getting the church app ready..."
        />
      }
    >
      <RouteErrorBoundary resetKey={location.pathname}>
        <AppRoutes />
      </RouteErrorBoundary>
    </Suspense>
  );
}

export default function App() {
  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <DynamicInstallMetadata />
      <AppShell />
    </Router>
  );
}

const routeErrorStyles = {
  page: {
    alignItems: "center",
    background: "#f8fafc",
    boxSizing: "border-box",
    color: "#0f172a",
    display: "grid",
    minHeight: "100dvh",
    padding: 20,
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 18,
    boxShadow: "0 18px 42px rgba(15, 23, 42, 0.08)",
    maxWidth: 680,
    padding: 24,
    width: "100%",
  },
  title: {
    fontSize: 24,
    margin: "0 0 10px",
  },
  error: {
    background: "#f1f5f9",
    borderRadius: 12,
    color: "#b91c1c",
    overflowX: "auto",
    padding: 14,
    whiteSpace: "pre-wrap",
  },
};
